/**
 * useDetailContent hook.
 */
import Context from '@shared/components/utils/context/detailContext';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import useTransform from '@shared/components/utils/hooks/useTransform';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useEffect } from 'react';
import { DELAY } from '@shared/types/constants/animations';

/**
 * Constants.
 *
 * @constant {number} VERTICAL_OFFSET - Vertical offset for the detail page.
 */
const VERTICAL_OFFSET = 150;

/**
 * Custom hook for the DetailContent component. Returns the properties required for the template.
 */
export default function useDetailContent() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();
  const { complete, focusable } = useContext(Context);

  // Selectors
  const { focus } = useAppSelector(focusSelector);

  // ------------------------------------------------------------
  // Custom hooks
  // ------------------------------------------------------------

  /**
   * Transform hook. Handles the vertical transform of the detail page.
   */
  const [verticalStyle, , , changeVerticalStyle] = useTransform(
    0,
    VERTICAL_OFFSET,
    Direction.VERTICAL
  );

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Handles the vertical style of the detail page.
   */
  useEffect(() => {
    if (!complete) {
      setTimeout(() => {
        changeVerticalStyle(0);
      }, DELAY.DETAIL_CONTENT);
    }
  }, [complete]);

  /**
   * Handles the vertical style of the detail page.
   */
  useEffect(() => {
    // Check if menu is focused and return if so
    if (focus === Focus.MENU) return;

    // Default vertical style
    let verticalStyle = 0;

    // Check if related is focused
    const areRelatedFocused = !!(
      (focus as number[])?.length &&
      (focus as number[])?.includes(parseInt(`${Focus.RAILS_SUB_LIST}${0}`))
    );
    if (areRelatedFocused) {
      verticalStyle = 1;
    }

    // Set new styles
    changeVerticalStyle(verticalStyle);
  }, [focus]);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    if (!focusable.current.buttons && focusable.current.synopsis) {
      dispatch(setFocus(Focus.DETAIL_SYNOPSIS));
    } else {
      dispatch(setFocus(Focus.DETAIL_BUTTONS));
    }
  }, []);

  // ------------------------------------------------------------
  // Return
  // ------------------------------------------------------------

  return {
    props: {
      animation: {
        animation: 'detail-scroll',
        style: verticalStyle,
      },
      container: {
        display: complete,
        onMouseEnter: onMouseEnter,
      },
    },
  };
}
